import type { SpecialtyPath } from '@/lib/moz-submission-utils';
import specialtyHierarchy from '../../../specialty-hierarchy.json';

/** The МОЗ specialty catalog: a leaf is a key whose value is null. */
export interface SpecialtyTree {
  [name: string]: SpecialtyTree | null;
}

const CATALOG = (specialtyHierarchy as { 'Цільова аудиторія': SpecialtyTree })['Цільова аудиторія'];

export interface AudienceGroup {
  /** Top-level catalog group, e.g. «Лікарські спеціальності». */
  category: string;
  /** Every specialty of the group is selected. */
  all: boolean;
  specialties: SpecialtyPath[];
}

function collectLeafKeys(node: SpecialtyTree, prefix: string[], out: Set<string>): void {
  for (const [name, child] of Object.entries(node)) {
    const path = [...prefix, name];
    if (child) collectLeafKeys(child, path, out);
    else out.add(path.join(' > '));
  }
}

const isPath = (value: unknown): value is SpecialtyPath =>
  !!value && typeof value === 'object'
  && Array.isArray((value as SpecialtyPath).path) && (value as SpecialtyPath).path.length > 0
  && typeof (value as SpecialtyPath).name === 'string';

/**
 * Selected specialties grouped by top-level catalog group, in catalog order.
 * Only the top level is used; the sub-profiles (загального профілю,
 * хірургічного профілю…) are not shown.
 */
export function groupTargetAudience(selected: unknown, catalog: SpecialtyTree = CATALOG): AudienceGroup[] {
  const paths = Array.isArray(selected) ? selected.filter(isPath) : [];
  const byCategory = new Map<string, SpecialtyPath[]>();
  for (const sp of paths) {
    const list = byCategory.get(sp.path[0]) ?? [];
    list.push(sp);
    byCategory.set(sp.path[0], list);
  }

  const order = [...Object.keys(catalog), ...[...byCategory.keys()].filter((c) => !(c in catalog))];
  return order
    .filter((category) => byCategory.has(category))
    .map((category) => {
      const specialties = byCategory.get(category)!;
      const subtree = catalog[category];
      let all = false;
      if (subtree) {
        const leaves = new Set<string>();
        collectLeafKeys(subtree, [category], leaves);
        const chosen = new Set(specialties.map((sp) => sp.path.join(' > ')));
        all = leaves.size > 0 && [...leaves].every((key) => chosen.has(key));
      }
      return { category, all, specialties };
    });
}

/**
 * Document form, one line per group:
 * «Лікарські спеціальності: усі», «Фармацевтичні спеціальності: Фармація, Клінічна фармація».
 */
export function formatTargetAudienceLines(selected: unknown, catalog: SpecialtyTree = CATALOG): string[] {
  return groupTargetAudience(selected, catalog).map((group) => {
    const names = group.all ? 'усі' : [...new Set(group.specialties.map((sp) => sp.name))].join(', ');
    return `${group.category}: ${names}`;
  });
}
